// What a source asks for and what it must answer, shared by both source modes.

import { validateRows } from '../config'
import { publishedFilters } from '../state/publish'

/**
 * What either source hands the table.
 * @typedef {object} SourceResult
 * @property {'client'|'page'} mode
 * @property {'loading'|'error'|'ready'} status
 * @property {object[]} data the loaded set: the complete client set, or the active page
 * @property {object[]} matching client: the complete matching set, sorted; page: the active page
 * @property {object[]} pageRows the visible page
 * @property {?number} total exact matching count; null until a load succeeds
 * @property {?number} eligibleTotal exact eligible matching count; null until a load succeeds
 * @property {Object<string, number>} aggregates summary values over the complete matching set
 * @property {number} currentPage
 * @property {number} firstShown
 * @property {number} lastShown
 * @property {() => void} retry repeats the last request with unchanged query, page and page size
 */

/**
 * One page-backed request: the committed query, page and page size. View state is not part of it, so a
 * View change issues no request.
 * @typedef {object} PageRequest
 * @property {string} tableId
 * @property {string} search
 * @property {Object<string, *>} filters
 * @property {{key: string, direction: string}} sort
 * @property {number} page
 * @property {number} pageSize at most this many rows may come back
 */

/** @returns {PageRequest} */
export const requestOf = (state, config) => ({
  tableId: config.tableId,
  search: state.query.search,
  filters: publishedFilters(state.query.filters),
  sort: { ...state.query.sort },
  page: state.page,
  pageSize: state.pageSize,
})

const isCount = value => Number.isInteger(value) && value >= 0

/**
 * Every problem with one page-backed response. A response supplies the active page's rows, the exact
 * matching count, the exact eligible count when selection is configured, and complete-query aggregates
 * when a summary row is configured.
 * @param {import('../types').NormalizedConfig} config
 * @param {PageRequest} request
 * @param {object} response {rows, total, eligibleTotal?, aggregates?}
 * @returns {string[]}
 */
export const checkResponse = (config, request, response) => {
  if (!response || typeof response !== 'object')
    return ['response: a page-backed source answers {rows, total}']
  const errors = []
  if (!isCount(response.total))
    errors.push(
      'total: a page-backed response supplies the exact matching count'
    )
  if (!Array.isArray(response.rows)) {
    errors.push('rows: the source must supply an array')
    return errors
  }
  validateRows(config, response.rows, {
    total: isCount(response.total) ? response.total : undefined,
    pageSize: request.pageSize,
  }).forEach(error => {
    if (!errors.includes(error)) errors.push(error)
  })
  if (config.selection && !isCount(response.eligibleTotal))
    errors.push(
      'eligibleTotal: a selectable page-backed response supplies the exact eligible count'
    )
  if (config.summary) {
    const aggregates = response.aggregates || {}
    ;(config.columns || [])
      .filter(column =>
        column.capabilities ? column.capabilities.summary : column.summary
      )
      .forEach(column => {
        if (typeof aggregates[column.key] !== 'number')
          errors.push(
            `aggregates.${column.key}: a page-backed summary supplies its complete-query value`
          )
      })
  }
  return errors
}
